import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * CartItemsComponent - handles cart UI and auto-removal of dependent free kit.
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #mutationObserver = null;
  _checkTimeout = null;

  // product IDs (update here if IDs change)
  static FREE_KIT_ID = 8092516155426;
  static SYSTEM_ID = 8041059713058;
  static FREE_TRAVEL_SET_ID = 8149863039010; //Currently used for Free Skinny Scarf
  static FREE_TRAVEL_SET_THRESHOLD = 100; // $100
  static OTHER_PRODUCT_ID = 8149861924898; //Currently used for Skinny Scarf

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // run initial check ASAP
    this.#checkFreeKitDependency();

    // fallback: observe DOM changes from third-party apps (Essential Upsell)
    this.#attachCartMutationObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#disconnectCartMutationObserver();
  }

  // ---------------------------
  // Auto-remove free kit helper
  // ---------------------------
  async #checkFreeKitDependency() {
  try {
    const FREE_KIT_ID = this.constructor.FREE_KIT_ID;
    const SYSTEM_ID = this.constructor.SYSTEM_ID;
    const FREE_TRAVEL_SET_ID = this.constructor.FREE_TRAVEL_SET_ID;
    const OTHER_PRODUCT_ID = this.constructor.OTHER_PRODUCT_ID;

    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();

    // FREE KIT logic
    const freeKitItem = cart.items.find((i) => Number(i.product_id) === Number(FREE_KIT_ID));
    const systemItem = cart.items.find((i) => Number(i.product_id) === Number(SYSTEM_ID));

    // Disabled auto-removal
    // if (freeKitItem && !systemItem) {
    //   await this.#removeCartItem(freeKitItem.key, 'auto-remove-free-kit');
    // }

    // FREE TRAVEL SET logic — no threshold
    const freeTravelSetItem = cart.items.find((i) => Number(i.product_id) === Number(FREE_KIT_ID));

    // --- Enforce max quantity = 1 for FREE_KIT_ID ---
    if (freeTravelSetItem && freeTravelSetItem.quantity > 1) {
      console.log('Limiting FREE_KIT_ID to 1');

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: freeTravelSetItem.key,
          quantity: 1
        })
      });

      return; // Important: stop the rest of the function from re-triggering logic
    }


    const otherProductItem = cart.items.find((i) => Number(i.product_id) === Number(OTHER_PRODUCT_ID));

    // If user has the free travel set but not qualified by your new rules, remove it.
    // Since threshold is gone, remove only if YOU want to (example below)
    // If you always want to keep it, REMOVE this block entirely.
    // if (freeTravelSetItem && !systemItem) {
    //   await this.#removeCartItem(freeTravelSetItem.key, 'auto-remove-free-travel-set');
    // }

    // if (systemItem && freeTravelSetItem && otherProductItem) {
    //   await this.#removeCartItem(otherProductItem.key, 'auto-remove-other-product');
    // }

    // Remove all OTHER_PRODUCT_ID logic (it depended on the threshold)
    // (Removed)

  } catch (err) {
    console.error('checkFreeKitDependency error', err);
  }
}

  // async #checkFreeKitDependency() {
  //   try {
  //     const FREE_KIT_ID = this.constructor.FREE_KIT_ID;
  //     const SYSTEM_ID = this.constructor.SYSTEM_ID;
  //     const FREE_TRAVEL_SET_ID = this.constructor.FREE_TRAVEL_SET_ID;
  //     const FREE_TRAVEL_SET_THRESHOLD = this.constructor.FREE_TRAVEL_SET_THRESHOLD;
  //     const OTHER_PRODUCT_ID = this.constructor.OTHER_PRODUCT_ID;

  //     const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  //     if (!cartRes.ok) return;
  //     const cart = await cartRes.json();

  //     // --- Handle FREE_KIT_ID ---
  //     const freeKitItem = cart.items.find((i) => Number(i.product_id) === Number(FREE_KIT_ID));
  //     const systemItem = cart.items.find((i) => Number(i.product_id) === Number(SYSTEM_ID));

  //     if (freeKitItem && !systemItem) {
  //       await this.#removeCartItem(freeKitItem.key, 'auto-remove-free-kit');
  //     }

  //     // --- Handle FREE_TRAVEL_SET_ID ---
  //     const freeTravelSetItem = cart.items.find((i) => Number(i.product_id) === Number(FREE_TRAVEL_SET_ID));

  //     if (freeTravelSetItem && cart.total_price < FREE_TRAVEL_SET_THRESHOLD * 100) {
  //       await this.#removeCartItem(freeTravelSetItem.key, 'auto-remove-free-travel-set');
  //     }

  //     // --- Handle OTHER_PRODUCT_ID when freeTravelSet is present and cart >= $100 ---
  //     const otherProductItem = cart.items.find((i) => Number(i.product_id) === Number(OTHER_PRODUCT_ID));

  //     if (freeTravelSetItem && otherProductItem && cart.total_price >= FREE_TRAVEL_SET_THRESHOLD * 100) {
  //       await this.#removeCartItem(otherProductItem.key, 'auto-remove-other-product');
  //     }

  //   } catch (err) {
  //     console.error('checkFreeKitDependency error', err);
  //   }
  // }


    async #removeCartItem(itemKey, source) {
    try {
      const body = JSON.stringify({ id: itemKey, quantity: 0 });
      const resText = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })).then((r) => r.text());
      const parsed = JSON.parse(resText);

      if (parsed.sections && parsed.sections[this.sectionId]) {
        morphSection(this.sectionId, parsed.sections[this.sectionId]);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      const newSectionHTML = new DOMParser().parseFromString(parsed.sections?.[this.sectionId] || '', 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source,
          sections: parsed.sections || {},
        })
      );
    } catch (err) {
      console.error(`#removeCartItem error (${source})`, err);
    }
  }


  #attachCartMutationObserver() {
    if (this.#mutationObserver) return;

    const root = document.querySelector('cart-drawer-component') || document.body;
    const observer = new MutationObserver(() => {
      if (this._checkTimeout) return;
      this._checkTimeout = setTimeout(() => {
        this.#checkFreeKitDependency();
        this._checkTimeout = null;
      }, 200);
    });

    observer.observe(root, { childList: true, subtree: true, attributes: false });
    this.#mutationObserver = observer;
  }

  #disconnectCartMutationObserver() {
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  // ---------------------------
  // Existing cart logic
  // ---------------------------
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // After we've updated the UI, re-check dependency (covers race with apps)
        this.#checkFreeKitDependency();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // re-check dependency after any incoming cart update
    this.#checkFreeKitDependency();
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
